import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { navSections, isNavItemActive } from './navigation';
import {
  NAV_COLLAPSE_STORAGE_KEY,
  parsePersistedCollapsed,
  resolveCollapsed,
  serializeCollapsed,
  type CollapsedMap,
} from './navState';
import { Logo } from './Logo';
import { cn } from '../ui';

/** Reads the persisted collapsed map from localStorage (SSR/blocked-safe). */
function readPersistedCollapsed(): CollapsedMap {
  if (typeof window === 'undefined') return {};
  try {
    return parsePersistedCollapsed(
      window.localStorage.getItem(NAV_COLLAPSE_STORAGE_KEY),
    );
  } catch {
    return {};
  }
}

/**
 * Fixed left navigation rail. Renders the grouped, collapsible sections from
 * {@link navSections}. Active state is computed against the current
 * pathname + query (so `/goals?framework=BHAG` highlights only the BHAG
 * quick-view, not the plain Goals item).
 *
 * Group collapse state is seeded from each section's `defaultOpen` and then
 * persisted per-group to localStorage, so a user's expand/collapse choices
 * survive reloads.
 */
export function Sidebar() {
  const sections = navSections();
  const location = useLocation();

  // Collapsed groups (by heading). Seeded from defaults layered with any
  // persisted overrides, then persisted on every change.
  const [collapsed, setCollapsed] = useState<CollapsedMap>(() =>
    resolveCollapsed(sections, readPersistedCollapsed()),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        NAV_COLLAPSE_STORAGE_KEY,
        serializeCollapsed(collapsed),
      );
    } catch {
      /* persistence is best-effort; ignore quota/availability errors */
    }
  }, [collapsed]);

  const toggle = (heading: string) =>
    setCollapsed((prev) => ({ ...prev, [heading]: !prev[heading] }));

  return (
    <aside className="fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-surface-border bg-surface-1">
      <div className="flex items-center px-5 py-4">
        <Logo size="md" showTagline />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2" aria-label="Primary">
        {sections.map((section, i) => {
          const isCollapsed = collapsed[section.heading] ?? section.defaultOpen === false;
          return (
            <div key={section.heading} className={cn(i > 0 && 'pt-2')}>
              <button
                type="button"
                onClick={() => toggle(section.heading)}
                aria-expanded={!isCollapsed}
                className="flex w-full items-center justify-between rounded-lg px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600 transition-colors hover:text-zinc-400"
              >
                {section.heading}
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 transition-transform',
                    isCollapsed && '-rotate-90',
                  )}
                  aria-hidden="true"
                />
              </button>
              {!isCollapsed && (
                <div className="mt-0.5 space-y-1">
                  {section.items.map(({ path, label, icon: Icon }) => {
                    const active = isNavItemActive(
                      path,
                      location.pathname,
                      location.search,
                    );
                    return (
                      <Link
                        key={path}
                        to={path}
                        className={cn(
                          'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                          active
                            ? 'bg-surface-3 text-zinc-50'
                            : 'text-zinc-400 hover:bg-surface-2 hover:text-zinc-200',
                        )}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-surface-border px-5 py-3">
        <p className="text-[11px] text-zinc-600">v1.0 · Dark</p>
      </div>
    </aside>
  );
}
